import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AutoCompleteUIDTO from "../dto/AutoCompleteUIDTO";
import AuthService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/AuthService";
import NotificationService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/NotificationService";
import Holiday from "../models/Holiday";
import WorkLog from "../models/WorkLog";
import AssignationService from "../services/AssignationService";
import HolidayService from "../services/HolidayService";
import { QueryKeyFactory, ServiceContext, WORKLOG_DAILY_LIST_CONTEXT } from "../services/queryKeys";
import UserService from "../services/UserService";
import WorkLogService from "../services/WorkLogService";
import ApiErrorUtils from "../utils/ApiErrorUtils";
import { workLogCategory } from "../utils/enums/WorkLogCategory";
import HolidayUtils from "../utils/HolidayUtils";
import OpsDateUtils from "../utils/OpsDateUtils";
import { unwrap } from "../utils/queryFn";
import { TABLE_QUERY_STALE_TIME } from "../utils/tableQuery";
import { userKeys } from "./useUserInfo";
import { pendingKey as pendingApprovalsKey } from "./useWorklogsApproval";

export const DEFAULT_DESCRIPTIONS = ["Internal Communication and Meetings", "Feature development", "Code review", "On-call"];
export const DEFAULT_COUNTRY = "AR";
export const RECENT_DAYS_LOOKBACK = 30;

const DEFAULT_HOURS = 8;
const DAYS_IN_WEEK = 7;
const VALIDATION_ERROR = "validation";

/** Every quick-log read lives under this context so one invalidation refreshes the whole tile. */
export const QUICK_LOG_CONTEXT: ServiceContext = { scope: "quick-log" };

const workLogKeys = new QueryKeyFactory(WorkLogService.path);
const assignationKeys = new QueryKeyFactory(AssignationService.path);
const holidayKeys = new QueryKeyFactory(HolidayService.path);

const NO_ASSIGNATIONS: AutoCompleteUIDTO[] = [];
const NO_HOLIDAYS: Holiday[] = [];

/** Prefer recent unique descriptions (most recent first), fill remaining slots with defaults. */
export const buildReuseDescriptions = (logs: WorkLog[], limit = 4): string[] => {
  const recentUnique: string[] = [];
  const seen = new Set<string>();

  const sorted = [...logs].sort((a, b) => new Date(b.shortDay).getTime() - new Date(a.shortDay).getTime());

  for (const log of sorted) {
    const desc = log.description?.trim();
    if (!desc || seen.has(desc)) continue;
    seen.add(desc);
    recentUnique.push(desc);
    if (recentUnique.length >= limit) break;
  }

  for (const fallback of DEFAULT_DESCRIPTIONS) {
    if (recentUnique.length >= limit) break;
    if (seen.has(fallback)) continue;
    seen.add(fallback);
    recentUnique.push(fallback);
  }

  return recentUnique;
};

const selectRecentDescriptions = (logs: WorkLog[] | null) => buildReuseDescriptions(logs || []);

export type QuickLogMode = "day" | "week";

interface QuickLogForm {
  description: string;
  hours: number;
  internalDescription: string;
  internalHours: number;
}

interface SaveDayInput {
  date: Date;
  assignationExternalId: string;
  buildPayload: (existing?: WorkLog) => WorkLog;
}

interface WeekResult {
  created: number;
  updated: number;
  failures: string[];
}

interface CopyResult {
  found: boolean;
  created: number;
  updated: number;
  skipped: number;
  skippedDaysOff: Set<string>;
}

interface UseQuickLogOptions {
  onWorklogCreated?: () => void;
}

/** Payload for the day typed in the tile. A blank internal description or zero internal hours copies the external ones. A worklog already on that day keeps its adjustments, bonus, category and details. */
export const buildWorkLogPayload = (date: Date, form: QuickLogForm, assignation: AutoCompleteUIDTO, base?: WorkLog): WorkLog => ({
  ...(base || {}),
  externalId: base?.externalId,
  shortDay: OpsDateUtils.toUTCWithSetHours(date),
  description: form.description.trim(),
  hours: form.hours,
  internalDescription: form.internalDescription.trim() || form.description.trim() || undefined,
  internalHours: form.internalHours === 0 ? form.hours : form.internalHours,
  adjustmentHours: base?.adjustmentHours ?? 0,
  bonusHours: base?.bonusHours ?? 0,
  assignationExternalId: assignation.externalId,
  assignationName: assignation.label,
  category: base?.category || workLogCategory.REGULAR,
  details: base?.details,
});

/** Payload that copies a worklog from last week onto `newDate`, keeping what the target day already carries. */
export const buildCopiedPayload = (log: WorkLog, newDate: Date, existing?: WorkLog): WorkLog => ({
  ...(existing || {}),
  externalId: existing?.externalId,
  shortDay: OpsDateUtils.toUTCWithSetHours(newDate),
  description: log.description,
  internalDescription: log.internalDescription,
  hours: log.hours,
  internalHours: log.internalHours,
  adjustmentHours: existing?.adjustmentHours ?? 0,
  bonusHours: existing?.bonusHours ?? 0,
  assignationExternalId: log.assignationExternalId,
  assignationName: log.assignationName,
  category: log.category || workLogCategory.REGULAR,
});

/**
 * Plain Error carrying the message the way ApiErrorUtils reads it. It is deliberately not an ApiError:
 * the app MutationCache toasts those, and here the caller owns the toast (batches show one summary).
 */
const toSaveError = (error: unknown, fallback: string): Error => {
  const message = (error instanceof Error && error.message) || fallback;
  return Object.assign(new Error(message), { response: { data: { message } } });
};

const orSaveError = async <T>(request: Promise<T>, fallback: string): Promise<T> => {
  try {
    return await request;
  } catch (error) {
    throw toSaveError(error, fallback);
  }
};

/** Always a fresh read: the cached day can be stale and a save must not create a duplicate. */
const findExistingForDay = async (
  userExternalId: string | undefined,
  assignationExternalId: string,
  date: Date,
): Promise<WorkLog | undefined> => {
  if (!userExternalId || !assignationExternalId) return undefined;

  const dayStr = moment(date).format("YYYY-MM-DD");
  const logs = await orSaveError(
    unwrap(WorkLogService.findWorkLogsByRequesterAndDates(userExternalId, dayStr, dayStr)),
    "Could not load existing worklogs",
  );
  const match = (logs || []).find(
    (log) =>
      log.assignationExternalId === assignationExternalId &&
      log.shortDay &&
      OpsDateUtils.isSameCalendarDay(OpsDateUtils.utcOffset(new Date(log.shortDay)), date),
  );
  return match && match.shortDay ? { ...match, shortDay: OpsDateUtils.utcOffset(new Date(match.shortDay)) } : undefined;
};

const saveDay = async (userExternalId: string | undefined, input: SaveDayInput): Promise<"created" | "updated"> => {
  const existing = await findExistingForDay(userExternalId, input.assignationExternalId, input.date);
  if (existing?.externalId) {
    await orSaveError(unwrap(WorkLogService.update(input.buildPayload(existing))), "Error updating worklog");
    return "updated";
  }

  await orSaveError(unwrap(WorkLogService.create(input.buildPayload())), "Error creating worklog");
  return "created";
};

/** Reads and writes behind the dashboard quick-log tile, plus the form that today's worklog hydrates. */
export const useQuickLog = ({ onWorklogCreated }: UseQuickLogOptions = {}) => {
  const queryClient = useQueryClient();
  const userExternalId = AuthService.getUser()?.externalId;

  const { today, month, year, dayStr } = useMemo(() => OpsDateUtils.localDay(), []);

  const [chosenAssignation, setChosenAssignation] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [hours, setHours] = useState<number>(DEFAULT_HOURS);
  const [internalDescription, setInternalDescription] = useState<string>("");
  const [internalHours, setInternalHours] = useState<number>(0);
  const [showInternal, setShowInternal] = useState(false);

  const userQuery = useQuery({
    queryKey: userKeys.detail(`core:${userExternalId ?? "anonymous"}`),
    queryFn: () => unwrap(UserService.findByCoreUserId(userExternalId!)),
    enabled: !!userExternalId,
    staleTime: TABLE_QUERY_STALE_TIME,
  });
  const userCountry: string = userQuery.data?.country || DEFAULT_COUNTRY;

  const assignationsQuery = useQuery({
    queryKey: assignationKeys.list({ userExternalId, month, year }, QUICK_LOG_CONTEXT),
    queryFn: () => unwrap(AssignationService.findAssignationsNamesByUser(userExternalId, today)),
    enabled: !!userExternalId,
  });
  const assignations = assignationsQuery.data ?? NO_ASSIGNATIONS;

  const selectedAssignation = assignations.some((a) => a.externalId === chosenAssignation)
    ? chosenAssignation
    : (assignations[0]?.externalId ?? "");

  // No logged user means the AR (country code) fallback is all we will ever know. Otherwise wait until the user query settles,
  // so a user from another country does not trigger a throwaway request for AR.
  const holidaysEnabled = !userExternalId || userQuery.isFetched;
  const holidaysQuery = useQuery({
    queryKey: holidayKeys.list({ month, country: userCountry }, QUICK_LOG_CONTEXT),
    queryFn: () => unwrap(HolidayService.findHolidaysOfMonthByCountry(month, userCountry)),
    enabled: holidaysEnabled,
  });
  const holidays = holidaysQuery.data ?? NO_HOLIDAYS;
  const holidaysReady = holidaysQuery.isSuccess || holidaysQuery.isError;

  const dayLogsQuery = useQuery({
    queryKey: workLogKeys.list({ userExternalId, from: dayStr, to: dayStr }, QUICK_LOG_CONTEXT),
    queryFn: () => unwrap(WorkLogService.findWorkLogsByRequesterAndDates(userExternalId!, dayStr, dayStr)),
    enabled: !!userExternalId && !!selectedAssignation,
  });

  const recentStart = moment(today).subtract(RECENT_DAYS_LOOKBACK, "days").format("YYYY-MM-DD");
  const recentQuery = useQuery({
    queryKey: workLogKeys.list({ userExternalId, from: recentStart, to: dayStr }, QUICK_LOG_CONTEXT),
    queryFn: () => unwrap(WorkLogService.findWorkLogsByRequesterAndDates(userExternalId!, recentStart, dayStr)),
    enabled: !!userExternalId,
    select: selectRecentDescriptions,
  });
  const recentDescriptions = recentQuery.data ?? DEFAULT_DESCRIPTIONS;

  const existingTodayWorkLog = useMemo((): WorkLog | null => {
    if (!selectedAssignation) return null;

    const match = (dayLogsQuery.data || []).find(
      (log) =>
        log.assignationExternalId === selectedAssignation &&
        log.shortDay &&
        OpsDateUtils.isSameCalendarDay(OpsDateUtils.utcOffset(new Date(log.shortDay)), today),
    );

    return match?.externalId && match.shortDay ? { ...match, shortDay: OpsDateUtils.utcOffset(new Date(match.shortDay)) } : null;
  }, [dayLogsQuery.data, selectedAssignation, today]);

  const alreadyLoggedToday = !!existingTodayWorkLog?.externalId;
  const isLoadingToday = dayLogsQuery.isPending;

  // Keyed on the saved fields, not the object. A refetch that fails structural sharing would otherwise
  // be a new object and wipe whatever the user is typing, even when the server data did not change.
  const todayLogKey = existingTodayWorkLog
    ? [
        existingTodayWorkLog.externalId,
        existingTodayWorkLog.description ?? "",
        existingTodayWorkLog.hours ?? DEFAULT_HOURS,
        existingTodayWorkLog.internalDescription ?? "",
        existingTodayWorkLog.internalHours ?? 0,
      ].join("\u0000")
    : "";
  const existingTodayRef = useRef(existingTodayWorkLog);
  existingTodayRef.current = existingTodayWorkLog;

  useEffect(() => {
    if (!selectedAssignation || isLoadingToday) return;

    const log = existingTodayRef.current;
    if (log) {
      setDescription(log.description || "");
      setHours(log.hours ?? DEFAULT_HOURS);
      setInternalDescription(log.internalDescription || "");
      setInternalHours(log.internalHours ?? 0);
      setShowInternal(!!(log.internalDescription || (log.internalHours && log.internalHours > 0)));
    } else {
      setDescription("");
      setHours(DEFAULT_HOURS);
      setInternalDescription("");
      setInternalHours(0);
      setShowInternal(false);
    }
  }, [selectedAssignation, isLoadingToday, todayLogKey]);

  /** Refetch every quick-log read; resolves once the active ones are back. */
  const refresh = useCallback(() => queryClient.invalidateQueries({ queryKey: workLogKeys.lists(QUICK_LOG_CONTEXT) }), [queryClient]);

  const selectedAssignationData = assignations.find((a) => a.externalId === selectedAssignation);

  /** What a successful write touches: the tile, the Work Logs screen and the approval tile. */
  const invalidateWriteViews = () =>
    Promise.all([
      refresh(),
      queryClient.invalidateQueries({ queryKey: workLogKeys.lists(WORKLOG_DAILY_LIST_CONTEXT) }),
      queryClient.invalidateQueries({ queryKey: pendingApprovalsKey }),
    ]);

  const saveDayMutation = useMutation({
    mutationFn: (input: SaveDayInput) => saveDay(AuthService.getUser()?.externalId, input),
    onSuccess: invalidateWriteViews,
  });

  const saveWeekMutation = useMutation({
    mutationFn: async (inputs: SaveDayInput[]): Promise<WeekResult> => {
      const result: WeekResult = { created: 0, updated: 0, failures: [] };

      for (const input of inputs) {
        try {
          const outcome = await saveDay(AuthService.getUser()?.externalId, input);
          if (outcome === "created") result.created++;
          else result.updated++;
        } catch (error: any) {
          const reason = ApiErrorUtils.getMessage(error, error?.message || "Unknown error");
          result.failures.push(`${moment(input.date).format("ddd D MMM")}: ${reason}`);
        }
      }

      return result;
    },
    onSuccess: invalidateWriteViews,
  });

  const copyLastWeekMutation = useMutation({
    mutationFn: async (requesterExternalId: string): Promise<CopyResult> => {
      const lastWeekStart = new Date(today);
      lastWeekStart.setDate(today.getDate() - DAYS_IN_WEEK);
      const lastWeekEnd = new Date(today);
      lastWeekEnd.setDate(today.getDate() - 1);

      const lastWeekLogs = await orSaveError(
        unwrap(
          WorkLogService.findWorkLogsByRequesterAndDates(
            requesterExternalId,
            moment(lastWeekStart).format("YYYY-MM-DD"),
            moment(lastWeekEnd).format("YYYY-MM-DD"),
          ),
        ),
        "Error copying last week's worklogs",
      );

      const result: CopyResult = {
        found: !!lastWeekLogs?.length,
        created: 0,
        updated: 0,
        skipped: 0,
        skippedDaysOff: new Set<string>(),
      };
      if (!result.found) return result;

      for (const log of lastWeekLogs) {
        if (!log.shortDay) {
          console.warn("Skipping worklog with missing shortDay:", log.externalId);
          result.skipped++;
          continue;
        }

        if (log.category === workLogCategory.DAY_OFF) {
          result.skippedDaysOff.add(moment(OpsDateUtils.utcOffset(new Date(log.shortDay))).format("ddd MM/DD"));
          continue;
        }

        const newDate = OpsDateUtils.utcOffset(new Date(log.shortDay));
        newDate.setDate(newDate.getDate() + DAYS_IN_WEEK);
        OpsDateUtils.setHoursToZero(newDate);

        if (OpsDateUtils.isWeekend(newDate) || HolidayUtils.isHoliday(newDate, holidays) || newDate > today) {
          result.skipped++;
          continue;
        }

        try {
          const outcome = await saveDay(requesterExternalId, {
            date: newDate,
            assignationExternalId: log.assignationExternalId,
            buildPayload: (existing) => buildCopiedPayload(log, newDate, existing),
          });
          if (outcome === "created") result.created++;
          else result.updated++;
        } catch (error) {
          console.error("Error copying worklog for date:", moment(newDate).format("YYYY-MM-DD"), error);
          result.skipped++;
        }
      }

      return result;
    },
    onSuccess: (result) => (result.created || result.updated ? invalidateWriteViews() : undefined),
  });

  const isSaving = saveDayMutation.isPending || saveWeekMutation.isPending || copyLastWeekMutation.isPending;

  /** Throws the "validation" error (after toasting the missing-fields one) so callers can stay silent about it. */
  const draftFor = (date: Date): SaveDayInput => {
    if (!selectedAssignation || !description.trim()) {
      NotificationService.error("Please fill in all required fields");
      throw new Error(VALIDATION_ERROR);
    }
    if (!selectedAssignationData) {
      throw new Error(VALIDATION_ERROR);
    }

    const form: QuickLogForm = { description, hours, internalDescription, internalHours };
    return {
      date,
      assignationExternalId: selectedAssignation,
      buildPayload: (existing) => buildWorkLogPayload(date, form, selectedAssignationData, existing),
    };
  };

  /** Logs (or updates) today, or every weekday of this week up to today that is not a holiday. */
  const logPrimary = async (mode: QuickLogMode) => {
    if (isSaving) return;
    if (mode === "week" && !holidaysReady) return;

    try {
      if (mode === "day") {
        const result = await saveDayMutation.mutateAsync(draftFor(today));
        NotificationService.info(result === "updated" ? "Worklog updated!" : "Worklog created!");
        onWorklogCreated?.();
        return;
      }

      const weekStart = OpsDateUtils.startOfWeekMonday(today);
      const days = OpsDateUtils.weekdaysInMonth(weekStart, today, today, (day) => HolidayUtils.isHoliday(day, holidays));
      const { created, updated, failures } = await saveWeekMutation.mutateAsync(days.map((day) => draftFor(day)));

      if (created || updated) {
        NotificationService.info(`Week log: ${created} created, ${updated} updated${failures.length ? `, ${failures.length} failed` : ""}`);
        if (failures.length) {
          NotificationService.error(`Some days failed — ${failures.slice(0, 3).join(" · ")}`);
        }
      } else if (failures.length) {
        NotificationService.error(`Could not log the week — ${failures.slice(0, 3).join(" · ")}`);
      } else {
        NotificationService.info("No weekdays to log yet");
      }

      onWorklogCreated?.();
    } catch (error: any) {
      if (error?.message !== VALIDATION_ERROR) {
        NotificationService.error(ApiErrorUtils.getMessage(error, "Error saving worklog"));
      }
    }
  };

  /** Copies last week's worklogs one week forward, skipping days off, weekends, holidays and days after today. */
  const copyLastWeek = async () => {
    if (isSaving || !holidaysReady) return;

    const requesterExternalId = AuthService.getUser()?.externalId;
    if (!requesterExternalId) {
      NotificationService.error("User not authenticated");
      return;
    }

    try {
      const { found, created, updated, skipped, skippedDaysOff } = await copyLastWeekMutation.mutateAsync(requesterExternalId);

      if (!found) {
        NotificationService.info("No worklogs found from last week to copy");
        return;
      }

      if (created || updated) {
        NotificationService.info(`Copy last week: ${created} created, ${updated} updated`);
        onWorklogCreated?.();
      }
      if (skipped > 0) {
        NotificationService.info(`Skipped ${skipped} day(s)`);
      }
      if (skippedDaysOff.size > 0) {
        NotificationService.info(`Days off from last week were not copied: ${[...skippedDaysOff].join(", ")}.`);
      }
    } catch (error) {
      console.error("Error in copyLastWeek:", error);
      NotificationService.error("Error copying last week's worklogs");
    }
  };

  return {
    today,
    assignations,
    selectedAssignation,
    selectAssignation: setChosenAssignation,
    holidays,
    holidaysReady,
    userCountry,
    recentDescriptions,
    existingTodayWorkLog,
    alreadyLoggedToday,
    isCheckingToday: dayLogsQuery.isFetching,
    isSaving,
    description,
    setDescription,
    hours,
    setHours,
    internalDescription,
    setInternalDescription,
    internalHours,
    setInternalHours,
    showInternal,
    setShowInternal,
    refresh,
    logPrimary,
    copyLastWeek,
  };
};
